<script lang="ts">
	import dayjs from 'dayjs';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { NewsRoutes } from '#lib/websites/news/constants/routes.js';
	import { page } from '$app/state';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { HeniNewsLogo } from '$global/assets/logos/HeniNewsLogo';
	import { Button } from '$global/components/Button';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import type { GlobalHomePageData } from '$routes/types';

	const dataCy = 'home-news';

	let data = $derived(page.data as GlobalHomePageData);
	let articles = $derived(
		(data.newsArticles || [])
			.filter((article): article is NonNullable<typeof article> =>
				Boolean(article)
			)
			.slice(0, 3)
	);
	let intro = $derived(page.data.globalContent['news_intro']);

	const getDate = (article: GlobalHomePageData['newsArticles'][number]) => {
		const date =
			article.publishDate || article.date_updated || article.date_created;
		return date ? dayjs(date).format('D MMMM YYYY') : '';
	};

	const getCategory = (article: GlobalHomePageData['newsArticles'][number]) =>
		article.categories?.[0]?.HENI_News_Categories_id?.name || 'HENI News';
</script>

<section
	data-cy={dataCy}
	class="min-w-0 overflow-hidden rounded border border-gray-200 bg-white p-6 lg:p-8"
>
	<div class="mb-6 flex items-start justify-between gap-6">
		<div class="min-w-0">
			<HeniNewsLogo class="mb-2 w-[150px]" />
			<Txt variant="body3" class="text-gray-600">{intro}</Txt>
		</div>

		<a
			data-cy={`${dataCy}-view-all-news-top`}
			href={GlobalRoutes.NewsHome}
			class="hidden shrink-0 sm:block"
		>
			<Button
				dataCy={`${dataCy}-view-all-news-top`}
				variant="secondary"
				size="md"
			>
				View HENI News
				{#snippet trailing()}<ChevronRightIcon />{/snippet}
			</Button>
		</a>
	</div>

	{#if articles.length}
		<div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{#each articles as article, index}
				<a
					href={article.url || GlobalRoutes.NewsHome}
					target="_blank"
					rel="noopener noreferrer"
					class={`group grid min-w-0 grid-cols-[96px_minmax(0,1fr)] overflow-hidden rounded border border-gray-200 sm:grid-cols-[140px_minmax(0,1fr)] lg:grid-cols-[112px_minmax(0,1fr)] xl:block ${index === 2 ? 'sm:col-span-2 lg:col-span-1' : ''}`}
					data-cy={`${dataCy}-story`}
				>
					{#if article.image?.filename_disk}
						<img
							src={getImageUrl(article.image.filename_disk, '512px')}
							alt={article.title || ''}
							class="h-full min-h-[112px] w-full object-cover xl:aspect-[4/3] xl:h-auto xl:min-h-0"
							loading="lazy"
						/>
					{:else}
						<div
							class="bg-gray-75 h-full min-h-[112px] xl:aspect-[4/3] xl:min-h-0"
						></div>
					{/if}

					<div class="min-w-0 self-center p-4">
						<div class="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
							<Txt variant="body3" class="text-blue-600">
								{getCategory(article)}
							</Txt>
							<Txt variant="body3" class="text-gray-500">
								{getDate(article)}
							</Txt>
						</div>

						<Txt
							variant="h6"
							component="h3"
							class="!text-base !leading-snug group-hover:underline sm:line-clamp-5 sm:!text-[15px] xl:line-clamp-3 xl:!text-lg"
						>
							{article.title}
						</Txt>
					</div>
				</a>
			{/each}

			<div
				class="grid gap-4 sm:col-span-2 sm:grid-cols-2 lg:col-span-3 xl:col-span-1 xl:grid-cols-1 xl:grid-rows-2"
			>
				<a
					href={NewsRoutes.Data}
					class="group flex min-h-[180px] flex-col justify-between rounded bg-gray-50 p-5"
					data-cy={`${dataCy}-data-callout`}
				>
					<div>
						<Txt
							variant="label4"
							class="mb-2 tracking-[.12em] text-red-600 uppercase"
						>
							HENI News Data
						</Txt>
						<Txt variant="h6" component="h3" class="mb-2 group-hover:underline">
							Explore art market data
						</Txt>
						<Txt variant="body3" class="text-gray-500">
							Auction records, artist rankings and sales by city.
						</Txt>
					</div>

					<Txt variant="label4" class="mt-6 underline">View HENI News Data</Txt>
				</a>

				<a
					href="/newsletter/news"
					class="group flex min-h-[180px] flex-col justify-between rounded bg-gray-50 p-5"
					data-cy={`${dataCy}-newsletter-callout`}
				>
					<div>
						<Txt
							variant="label4"
							class="mb-2 tracking-[.12em] text-blue-600 uppercase"
						>
							HENI News Newsletter
						</Txt>
						<Txt variant="h6" component="h3" class="mb-2 group-hover:underline">
							Art news, straight to your inbox
						</Txt>
						<Txt variant="body3" class="text-gray-500">
							Get free daily art news and intelligence.
						</Txt>
					</div>

					<Txt variant="label4" class="mt-6 underline">
						View HENI News Newsletter
					</Txt>
				</a>
			</div>
		</div>
	{/if}

	<div class="mt-5 sm:hidden">
		<a data-cy={`${dataCy}-view-all-news-bottom`} href={GlobalRoutes.NewsHome}>
			<Button
				dataCy={`${dataCy}-view-all-news-bottom`}
				variant="secondary"
				size="md"
				class="w-full"
			>
				View HENI News
				{#snippet trailing()}<ChevronRightIcon />{/snippet}
			</Button>
		</a>
	</div>
</section>
